// ═══════════════════════════════════════════════════════════════════
// plazaFixtures3D.jsx — THE PLAZA'S FIXTURES, BUILT IN BLENDER   (caP'n, 2026-09-22)
// ═══════════════════════════════════════════════════════════════════
// Each fixture is a glb off plaza_1.webp (builders:
// gamemaster/models/sculpt/builders/plaza_props_blender/), worn through
// plazaProp3D's one loader. Every `place` below is its painted twin's OWN
// placement maths from plazaPieces3D, so a fixture stands exactly where its
// twin did and MOVES WITH plazaZones — moving a kiosk is still a data edit.
// The painted twins are the fail-safe (loading, failure, the sculpt gate).
// What is live stays live: the lantern's light and flicker, the forge.
// ═══════════════════════════════════════════════════════════════════
import { useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import {
  BulletinBoard, WillyBench, SweetTreatsStall, PackKiosk, WorkshopTent, rectBounds, noticeTex, signTex, runeBandTex, cardFaceTex, tentTex, LIGHT, G, C,
} from "./plazaPieces3D";
import { makeSculptedProp } from "./plazaProp3D";
import { ROWS } from "./plazaZones";

// ── THE BULLETIN BOARD ─────────────────────────────────────────────
export const SculptedBulletin = makeSculptedProp({
  file: "plaza_bulletin.glb", what: "the bulletin board", Fallback: BulletinBoard,
  place: ({ rect }) => {
    const b = rectBounds(rect); if (!b) return null;
    const S = Math.min(b.w, 3) / 3 * 1.15;                      // BulletinBoard's own S
    return {
      pos: [b.cx, 0, b.cz], scale: S, skinKey: "notices",
      skin: { slots: { noticeA: noticeTex(0), noticeB: noticeTex(1), noticeC: noticeTex(2), noticeD: noticeTex(3), noticeE: noticeTex(4) } },
    };
  },
});

// ── WILLY'S BENCH (built at L = 2.52, a 6-wide box) ───────────────
const BENCH_L = 2.52;
function BenchLive() {
  const flame = useRef();
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    if (flame.current) flame.current.intensity = G(LIGHT.lantern) * (1 + Math.sin(t * 7) * 0.05 + Math.sin(t * 13) * 0.03);
  });
  // the one light this side of the room, where the lantern stands
  return <pointLight ref={flame} position={[-BENCH_L / 2 + 0.35, 1.15, -0.1]} intensity={G(LIGHT.lantern)} distance={6} decay={1.4} color="#ffc46a" />;
}
export const SculptedBench = makeSculptedProp({
  file: "plaza_bench.glb", what: "Willy's bench", Fallback: WillyBench, Live: BenchLive,
  place: ({ rect }) => {
    const b = rectBounds(rect); if (!b) return null;
    return { pos: [b.cx + b.w * 0.12, 0, b.cz], scale: [(b.w * 0.42) / BENCH_L, 1, 1] };   // WillyBench's own x, z and L
  },
});

// ── SWEET TREATS — V'yen's booth (her coil is HER body, in plazaFolk3D) ──
export const SculptedStall = makeSculptedProp({
  file: "plaza_stall.glb", what: "Sweet Treats", Fallback: SweetTreatsStall,
  place: ({ rects }) => {
    const b = rectBounds(rects); if (!b) return null;
    return {
      pos: [b.x1 + 1 - 2.6, 0, b.y0 + 1.3], skinKey: "sign",               // SweetTreatsStall's own bx, bz
      skin: { slots: { sign: signTex("SWEET\nTREATS", "#f4e8d0", "#7a4a2a", 256, 160, "bold 46px Georgia, serif") }, noInk: /^glass/ },
    };
  },
});

// ── VENUS'S CARD-PACK KIOSK (built for a 6 x 3 box) ────────────────
const KIOSK_D = 3 * 0.9 / 0.8;
const CANDLES = [[1.25, 0.22], [1.45, 0.3], [1.62, 0.18]];
function KioskLive() {
  const flames = useRef([]);
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    for (let i = 0; i < flames.current.length; i++) { const f = flames.current[i]; if (f) f.scale.setScalar(0.85 + Math.sin(t * 9 + i * 2) * 0.15 + Math.sin(t * 17 + i) * 0.08); }
  });
  return CANDLES.map(([x, h], i) => (
    <group key={i} position={[x, 0.95, KIOSK_D / 2 - 0.22]}>
      <mesh ref={(el) => { flames.current[i] = el; }} position={[0, h + 0.06, 0]} scale={[1, 1.5, 1]}><sphereGeometry args={[0.03, 6, 6]} /><meshBasicMaterial color="#ffd27a" toneMapped={false} /></mesh>
      <mesh position={[0, h + 0.06, 0]}><sphereGeometry args={[0.1, 8, 6]} /><meshBasicMaterial color="#ffb040" transparent opacity={0.12} depthWrite={false} toneMapped={false} /></mesh>
    </group>
  ));
}
const turned = (t) => { const r = t.clone(); r.center.set(0.5, 0.5); r.rotation = Math.PI / 2; r.wrapS = r.wrapT = THREE.RepeatWrapping; r.repeat.set(4, 1); r.needsUpdate = true; return r; };
export const SculptedKiosk = makeSculptedProp({
  file: "plaza_kiosk.glb", what: "the pack kiosk", Fallback: PackKiosk, Live: KioskLive,
  place: ({ rect }) => {
    const b = rectBounds(rect); if (!b) return null;
    // a box taller than wide = the kiosk turned onto the west wall, glass facing east (2026-09-22)
    const turn = b.d > b.w, LW = turn ? b.d : b.w, LD = turn ? b.w : b.d;
    const S = Math.min(LW, 6) / 6 * 0.8;                                    // PackKiosk's own S
    const band = runeBandTex().clone(); band.wrapS = THREE.RepeatWrapping; band.repeat.set(2, 1); band.needsUpdate = true;
    return {
      pos: [b.cx, 0, b.cz], rot: turn ? [0, Math.PI / 2, 0] : undefined, scale: [S, S, S * (LD * 0.9 / 0.8) / KIOSK_D], skinKey: "kiosk",
      skin: {
        noInk: /^glass/,
        slotColor: { lintelrunes: "#6a6078", pierrunes: "#6a6078" },     // carved in the dark stone, not lit like paper
        slots: {
          lintelrunes: band, pierrunes: turned(runeBandTex()),
          sign: signTex("VENUS'S\nCARD PACKS\nBUY A PACK!", "#3a2a1a", "#e8d8b0", 256, 256, "bold 34px Georgia, serif"),
          cardA: cardFaceTex("#c9a24c"), cardB: cardFaceTex("#7c3a9c"), cardC: cardFaceTex("#2e6a9a"),
        },
      },
    };
  },
});

// ── WILLIE'S WORKSHOP — the tattered tent, sized to the ROOM (v2, 2026-09-22) ──
// caP'n: "the Forge ... inches up to the fountain ... so hard to walk right there".
// The glb reads its own footprint from plazaZones (build_tent.py): it spans from
// the forge box's north edge to the south balustrade, BUILT at w 10 / span 5.1.
// A different box scales it; the numbers below must match the builder's print.
const TENT_W = 10, TENT_SPAN = 5.1, TENT_WALL = ROWS - 0.75;          // the balustrade's inner face
const TENT_IN = 0.85, TENT_FORGE = [-0.4, 0, -0.25];
const CRYSTALS = [                                                       // on the open crate, and a spill beside it
  [-2.4, 1.07, -0.85, 0.11], [-2.3, 1.07, -0.78, 0.07], [-2.5, 1.07, -0.8, 0.06], [-2.38, 1.07, -0.95, 0.08],
  [-3.3, 0, 0.35, 0.13], [-3.15, 0, 0.45, 0.08], [-3.4, 0, 0.5, 0.07],
];
function TentLive({ rects, pl }) {
  const fire = useRef(); const ember = useRef(); const light = useRef(); const gems = useRef([]);
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    const k = 1 + Math.sin(t * 5.3) * 0.08 + Math.sin(t * 9.1) * 0.05;
    if (fire.current) { fire.current.scale.set(1, k, 1); fire.current.material.opacity = 0.75 + Math.sin(t * 7) * 0.1; }
    if (ember.current) ember.current.material.emissiveIntensity = 0.8 + Math.sin(t * 2.2) * 0.25;
    if (light.current) light.current.intensity = G(LIGHT.forge) * k;
    for (let i = 0; i < gems.current.length; i++) { const g = gems.current[i]; if (g) g.material.emissiveIntensity = 0.6 + Math.sin(t * 1.9 + i) * 0.3; }
  });
  const b = rectBounds(rects);
  const s = pl.scale;
  // the door mat of light at the mouth (west, where the zone walks you in), in world terms
  const door = b ? [(b.x0 + 0.1 - pl.pos[0]) / s[0], 0.02, (b.y1 + 0.5 - pl.pos[2]) / s[2]] : [0, 0.02, 0];
  return (
    <>
      <group position={TENT_FORGE} scale={TENT_IN}>
        <mesh ref={ember} position={[0, 0.662, 0]} rotation={[-Math.PI / 2, 0, 0]}><circleGeometry args={[0.52, 10]} /><meshStandardMaterial color="#6a3ae0" emissive="#7c4cff" emissiveIntensity={0.9} toneMapped={false} /></mesh>
        <mesh ref={fire} position={[0, 0.95, 0]}><coneGeometry args={[0.34, 0.7, 7]} /><meshBasicMaterial color="#9fe8ff" transparent opacity={0.8} depthWrite={false} toneMapped={false} /></mesh>
        <pointLight ref={light} position={[0, 1.2, 0]} intensity={G(LIGHT.forge)} distance={9} decay={1.4} color="#7ac8ff" />
      </group>
      {CRYSTALS.map(([x, y, z, r], i) => (
        <mesh key={i} ref={(el) => { gems.current[i] = el; }} position={[x, y * TENT_IN + r * 0.8, z]} rotation={[0.3 * i, i * 1.3, 0.2]}>
          <octahedronGeometry args={[r, 0]} />
          <meshStandardMaterial color={C.nullus} emissive={C.nullus} emissiveIntensity={0.7} roughness={0.2} toneMapped={false} />
        </mesh>
      ))}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={door}>
        <circleGeometry args={[0.9, 12]} />
        <meshBasicMaterial color="#7ac8ff" transparent opacity={0.06} depthWrite={false} toneMapped={false} />
      </mesh>
    </>
  );
}
export const SculptedTent = makeSculptedProp({
  file: "plaza_tent.glb", what: "the workshop tent", Fallback: WorkshopTent, Live: TentLive,
  place: ({ rects }) => {
    const b = rectBounds(rects); if (!b) return null;
    const span = TENT_WALL - 0.15 - b.y0;                                 // north edge of the box -> the wall
    const canvas = tentTex().clone(); canvas.repeat.set(1, 1);
    return {
      pos: [b.cx, 0, b.y0 + span / 2], scale: [b.w / TENT_W, b.w / TENT_W, span / TENT_SPAN],
      skinKey: "tent", skin: { maps: { canvas }, mapTint: "#d6c6a8" },    // weathered toward umber, as painted
    };
  },
});
